/**
 * JourneyView — DRR-309 Phase 2-4, Phase 8
 *
 * Goal-centric workflow visualization component.
 * Shows subject's journey through a workflow with tracks, progress, and next actions.
 *
 * 100% domain-agnostic: all labels come from backend config (workflow.displayName,
 * fragment.displayName, task.title). Zero hardcoded "admission", "resident", etc.
 *
 * Phase 4: Real-time subscriptions for live updates
 * Phase 8: Responsive breakpoints for mobile/tablet/desktop
 */

import React, { useCallback, useEffect } from 'react';
import {
  Stack,
  Group,
  Paper,
  Text,
  Alert,
  Skeleton,
  Badge,
  Button,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconRefresh,
  IconArrowRight,
  IconGitBranch,
} from '@tabler/icons-react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

import { useJourneyView } from './hooks/useJourneyView';
import { useRegulatoryDeadlines } from './hooks/useRegulatoryDeadlines';
import { useJourneyOverlay } from './overlays/JourneyOverlayProvider';
import { JourneyHeader } from './components/JourneyHeader';
import { JourneyProgress } from './components/JourneyProgress';
import { TracksOverview } from './components/TracksOverview';
import { NextActionCard } from './components/NextActionCard';
import { CompletionCelebration } from './components/CompletionCelebration';
import { MidJourneySpawnModal } from './components/MidJourneySpawnModal';
import type { JourneyViewProps } from './types';

// Mutation for firing workflow transitions
const FIRE_EVENT = gql`
  mutation FireWorkflowEvent($tenantId: String!, $input: GqlTransitionInstanceInput!) {
    transitionInstance(tenantId: $tenantId, input: $input) {
      id
      currentState
      status
      progressPercent
    }
  }
`;

export function JourneyView({
  instanceId,
  tenantId,
  compact = false,
  showTracks = true,
  showNextAction = true,
  onTransition,
  onTaskClick,
  onComplete,
}: JourneyViewProps) {
  // Phase 8: Responsive breakpoints
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');

  // Phase 4: Use real-time subscriptions with fallback polling
  const journey = useJourneyView(tenantId ?? '', instanceId, {
    pollInterval: 30000, // Fallback poll every 30s if subscription fails
    enableSubscription: true, // DRR-309 Phase 4: Enable real-time updates
  });

  // Compute regulatory deadlines from journey tasks
  const regulatoryDeadlines = useRegulatoryDeadlines({
    tasks: journey.regulatoryTasks,
    journeyStartedAt: journey.startedAt,
  });

  // Get overlay context to set deadlines
  const { setDeadlines } = useJourneyOverlay();

  // Sync computed deadlines to overlay provider
  useEffect(() => {
    setDeadlines(regulatoryDeadlines);
  }, [regulatoryDeadlines, setDeadlines]);

  const [fireEvent, { loading: firingEvent }] = useMutation(FIRE_EVENT);
  const [spawnModalOpen, setSpawnModalOpen] = React.useState(false);

  // Handle transition click
  const handleTransition = useCallback(
    async (event: string) => {
      if (onTransition) {
        onTransition(event);
        return;
      }

      try {
        await fireEvent({
          variables: {
            tenantId,
            input: {
              instanceId,
              event,
            },
          },
        });
      } catch (err) {
        console.error('Failed to fire event:', err);
      }
    },
    [fireEvent, instanceId, tenantId, onTransition]
  );

  // Handle next action click
  const handleNextAction = useCallback(() => {
    if (!journey.nextAction) return;

    if (journey.nextAction.type === 'transition') {
      handleTransition(journey.nextAction.id);
    } else if (journey.nextAction.type === 'task' && onTaskClick) {
      onTaskClick(journey.nextAction.id);
    }
  }, [journey.nextAction, handleTransition, onTaskClick]);

  // Handle track click
  const handleTrackClick = useCallback(
    (trackId: string) => {
      const track = journey.tracks.find((t) => t.trackId === trackId);
      if (track && track.instanceId) {
        // Navigate to track's instance
        console.log('Navigate to track instance:', track.instanceId);
      }
    },
    [journey.tracks]
  );

  // Loading state
  if (journey.loading && !journey.instanceId) {
    if (compact) {
      return (
        <Group gap="sm">
          <Skeleton height={20} width={150} />
          <Skeleton height={20} width={60} circle />
        </Group>
      );
    }

    return (
      <Stack gap="md">
        <Skeleton height={80} radius="md" />
        <Skeleton height={120} radius="md" />
        <Skeleton height={100} radius="md" />
      </Stack>
    );
  }

  // Error state
  if (journey.errorMessage) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
        <Group justify="space-between">
          <div>
            <Text fw={500}>Journey Error</Text>
            <Text size="sm">{journey.errorMessage}</Text>
          </div>
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconRefresh size={14} />}
          >
            Retry
          </Button>
        </Group>
      </Alert>
    );
  }

  // Completed state
  if (journey.isComplete) {
    if (compact) {
      return (
        <Group gap="sm">
          <Badge color="green" variant="filled">
            Complete
          </Badge>
          <Text size="sm" truncate>
            {journey.subjectName}'s {journey.goalName}
          </Text>
        </Group>
      );
    }

    return (
      <CompletionCelebration
        subjectName={journey.subjectName}
        goalName={journey.goalName}
        completedAt={journey.completedAt ?? new Date()}
        totalTasks={journey.totalTasks}
        daysTaken={journey.dayNumber}
        onContinue={onComplete}
      />
    );
  }

  // Compact mode
  if (compact) {
    return (
      <Paper p="sm" withBorder>
        <Stack gap="xs">
          <JourneyHeader
            subjectName={journey.subjectName}
            goalName={journey.goalName}
            status={journey.status}
            dayNumber={journey.dayNumber}
            startedAt={journey.startedAt}
            compact
          />

          <Group justify="space-between">
            <JourneyProgress
              progressPercent={journey.progressPercent}
              totalTasks={journey.totalTasks}
              completedTasks={journey.completedTasks}
              status={journey.status}
              compact
            />

            {showTracks && journey.tracks.length > 0 && (
              <TracksOverview tracks={journey.tracks} compact />
            )}

            {showNextAction && journey.nextAction && (
              <Button
                size="xs"
                variant="light"
                color={journey.nextAction.priority === 'critical' ? 'red' : 'blue'}
                rightSection={<IconArrowRight size={12} />}
                onClick={handleNextAction}
                loading={firingEvent}
              >
                {journey.nextAction.title}
              </Button>
            )}
          </Group>
        </Stack>
      </Paper>
    );
  }

  // Full mode with responsive layout (Phase 8)
  return (
    <Stack gap={isMobile ? 'sm' : 'md'}>
      {/* Header */}
      <JourneyHeader
        subjectName={journey.subjectName}
        goalName={journey.goalName}
        goalDescription={isMobile ? undefined : journey.goalDescription}
        status={journey.status}
        dayNumber={journey.dayNumber}
        startedAt={journey.startedAt}
        completedAt={journey.completedAt}
        compact={isMobile}
      />

      {/* Progress */}
      <JourneyProgress
        progressPercent={journey.progressPercent}
        totalTasks={journey.totalTasks}
        completedTasks={journey.completedTasks}
        status={journey.status}
        compact={isMobile}
      />

      {/* Tracks (if multi-track workflow) - hide on mobile if not primary content */}
      {showTracks && journey.tracks.length > 0 && (
        <TracksOverview
          tracks={journey.tracks}
          onTrackClick={handleTrackClick}
          compact={isMobile || isTablet}
        />
      )}

      {/* Next Action */}
      {showNextAction && journey.nextAction && (
        <NextActionCard
          action={journey.nextAction}
          onAction={handleNextAction}
          compact={isMobile}
        />
      )}

      {/* Blocked state */}
      {journey.isBlocked && !journey.nextAction?.isBlocked && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="yellow"
          variant="light"
          p={isMobile ? 'xs' : 'md'}
        >
          <Text size={isMobile ? 'xs' : 'sm'}>
            {isMobile
              ? 'Journey blocked. Complete required tasks.'
              : 'This journey is currently blocked. Complete the required tasks or wait for prerequisites to be met.'}
          </Text>
        </Alert>
      )}

      {/* Start Related Journey - smaller on mobile */}
      <Button
        variant="subtle"
        color="violet"
        size={isMobile ? 'xs' : 'sm'}
        leftSection={<IconGitBranch size={isMobile ? 14 : 16} />}
        onClick={() => setSpawnModalOpen(true)}
        fullWidth={isMobile}
      >
        {isMobile ? 'Related Journey' : 'Start Related Journey'}
      </Button>

      {/* Available transitions (when no clear next action) */}
      {!journey.nextAction &&
        journey.availableTransitions.length > 0 &&
        journey.availableTransitions.some((t) => t.guardPassed) && (
          <Paper p={isMobile ? 'sm' : 'md'} withBorder>
            <Stack gap="sm">
              <Text size="sm" fw={500}>
                Available Actions
              </Text>
              <Group gap="xs" wrap="wrap">
                {journey.availableTransitions
                  .filter((t) => t.guardPassed)
                  .map((transition) => (
                    <Button
                      key={transition.event}
                      variant="light"
                      size={isMobile ? 'xs' : 'sm'}
                      onClick={() => handleTransition(transition.event)}
                      loading={firingEvent}
                      fullWidth={isMobile}
                    >
                      {transition.displayName}
                    </Button>
                  ))}
              </Group>
            </Stack>
          </Paper>
        )}

      {/* Mid-Journey Spawn Modal */}
      <MidJourneySpawnModal
        opened={spawnModalOpen}
        onClose={() => setSpawnModalOpen(false)}
        parentInstanceId={instanceId}
        parentWorkflowName={journey.goalName}
        subjectName={journey.subjectName}
        tenantId={tenantId ?? ''}
        onSpawnSuccess={(newInstanceId) => {
          console.log('Spawned related journey:', newInstanceId);
        }}
      />
    </Stack>
  );
}

export default JourneyView;
